/**
 * Trunnion AI - Settings Page
 */

import { useState } from 'react';
import {
  Palette,
  Bell,
  Keyboard,
  User,
  Info,
} from 'lucide-react';
import { AppearanceSettings } from '@/components/settings/AppearanceSettings';
import { NotificationSettings } from '@/components/settings/NotificationSettings';
import { KeyboardShortcutsSettings } from '@/components/settings/KeyboardShortcutsSettings';
import { SessionInfo } from '@/components/settings/SessionInfo';
import { AboutSection } from '@/components/settings/AboutSection';

const tabs = [
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'shortcuts', label: 'Shortcuts', icon: Keyboard },
  { id: 'session', label: 'Session', icon: User },
  { id: 'about', label: 'About', icon: Info },
] as const;

type TabId = (typeof tabs)[number]['id'];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('appearance');

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-2xl font-bold text-white mb-6">Settings</h1>

      <div className="flex gap-6">
        {/* Tab navigation */}
        <nav className="w-48 shrink-0 space-y-1">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'bg-trunnion-gold-500/10 text-trunnion-gold-500'
                  : 'text-trunnion-slate-300 hover:bg-trunnion-slate-800 hover:text-white'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>

        {/* Tab content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'appearance' && <AppearanceSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'shortcuts' && <KeyboardShortcutsSettings />}
          {activeTab === 'session' && <SessionInfo />}
          {activeTab === 'about' && <AboutSection />}
        </div>
      </div>
    </div>
  );
}
